// src/components/AdminProposal.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
  updateDoc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase';
import { buildFunctionUrl } from '../../config/functions';
import { useAuth } from '../../components/AuthContext';
import { InformationCircleIcon, TrashIcon, PlusIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';

const AdminProposal = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const firebaseAuth = getAuth();
  const { userRole } = useAuth();

  const [client, setClient] = useState(null);
  const emptyItem = { name: '', description: '', quantity: 1, price: '' };
  const [items, setItems] = useState([emptyItem]);
  const [deposit, setDeposit] = useState(0);
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [intakeQuotes, setIntakeQuotes] = useState([]);
  const [loadingIntake, setLoadingIntake] = useState(true);
  const [editingProposal, setEditingProposal] = useState(null);
  const [currentUser, setCurrentUser] = useState(() => firebaseAuth.currentUser);

  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged((user) => setCurrentUser(user));
    return () => unsub();
  }, [firebaseAuth]);

  const resetForm = () => {
    setItems([{ ...emptyItem }]);
    setDeposit(0);
    setEditingProposal(null);
  };

  const fetchProposals = async (clientEmail) => {
    if (!clientEmail) return;
    setLoadingProposals(true);
    try {
      const proposalsQuery = query(
        collection(db, 'proposalTokens'),
        where('clientInfo.email', '==', clientEmail.toLowerCase())
      );
      const snapshot = await getDocs(proposalsQuery);
      const list = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
      setProposals(list);
    } catch (err) {
      console.error('Error al cargar propuestas del cliente:', err);
    } finally {
      setLoadingProposals(false);
    }
  };

  const fetchIntakeQuotes = async (clientDocId) => {
    if (!clientDocId) return;
    setLoadingIntake(true);
    try {
      const intakeQuery = query(
        collection(db, 'clients', clientDocId, 'intakeQuotes'),
        orderBy('capturedAt', 'desc')
      );
      const snapshot = await getDocs(intakeQuery);
      const list = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setIntakeQuotes(list);
    } catch (err) {
      console.error('Error al cargar solicitudes de cotización:', err);
    } finally {
      setLoadingIntake(false);
    }
  };

  useEffect(() => {
    if (!clientId) return navigate('/admin-clients');

    const loadClient = async () => {
      try {
        const rawId = decodeURIComponent(clientId);
        const directRef = doc(db, 'clients', rawId);
        let snap = await getDoc(directRef);
        let resolvedId = directRef.id;

        if (!snap.exists()) {
          const emailCandidate = rawId.includes('@') ? rawId.toLowerCase() : null;
          let fallback = null;

          if (emailCandidate) {
            const emailSnap = await getDocs(query(collection(db, 'clients'), where('email', '==', emailCandidate), limit(1)));
            if (!emailSnap.empty) fallback = emailSnap.docs[0];
          }

          if (!fallback) {
            const uidSnap = await getDocs(query(collection(db, 'clients'), where('uid', '==', rawId), limit(1)));
            if (!uidSnap.empty) fallback = uidSnap.docs[0];
          }

          if (!fallback) {
            throw new Error('Cliente no encontrado');
          }

          snap = fallback;
          resolvedId = fallback.id;
        }

        const data = snap.data();
        const clientPayload = {
          uid: resolvedId,
          name: data.businessName || data.name || '',
          phone: data.phone || '',
          email: data.email || '',
        };
        setClient(clientPayload);
        await Promise.all([fetchProposals(data.email), fetchIntakeQuotes(resolvedId)]);
      } catch (err) {
        console.error('Error al cargar cliente:', err);
        navigate('/admin/clients');
      }
    };

    loadClient();
  }, [clientId, navigate]);

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const removeItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const handleEditProposal = (proposal) => {
    setEditingProposal(proposal);
    setItems(
      (proposal.items && proposal.items.length > 0 ? proposal.items : [emptyItem]).map((item) => ({
        name: item.name || '',
        description: item.description || '',
        quantity: item.quantity || 1,
        price: item.price || '',
      }))
    );
    setDeposit(proposal.deposit ?? 0);
  };

  const formatSeriesLabel = (series) => {
    if (!series) return '';
    const match = String(series).toLowerCase().includes('lama')
      ? series.replace(/lama/gi, '').trim()
      : series;
    const numeric = match ? `Lama ${match}` : series;
    return numeric;
  };

  const handleImportIntakeQuote = (quote) => {
    const payload = quote?.payload || {};
    const models = Array.isArray(payload.models) ? payload.models : [];
    if (!models.length) {
      alert('Esta solicitud no incluye modelos estructurados.');
      return;
    }

    const mapped = models.map((model) => {
      const seriesLabel = model.series ? formatSeriesLabel(model.series) : '';
      const dimensions =
        model.height && model.width ? `${model.height}" × ${model.width}"` : 'Medidas por confirmar';
      const descriptors = [
        seriesLabel,
        dimensions,
        model.color || payload?.finishes?.color || null,
        model.glass || payload?.finishes?.glass || null,
      ]
        .filter(Boolean)
        .join(' · ');

      return {
        name: `${model.categoryName || model.categoryId || ''} ${model.name || 'Modelo'}`.trim() || 'Modelo solicitado',
        description: descriptors || model.notes || '',
        quantity: Number(model.quantity) || 1,
        price: Number(model.estimate || payload.windowEstimate || 0),
      };
    });

    setItems(mapped.length ? mapped : [{ ...emptyItem }]);
    setDeposit(0);
    setEditingProposal(null);
  };

  const formatCurrency = (value = 0) =>
    new Intl.NumberFormat('es-PR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(Number(value) || 0);

  const total = items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 1)), 0);
  const depositNumber = parseFloat(deposit || 0);
  const remaining = total - depositNumber;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client) return;

    const clientInfo = {
      uid: client.uid,
      name: client.name,
      phone: client.phone,
      email: client.email,
      deposit: depositNumber,
    };
    const createdBy = {
      uid: currentUser?.uid || null,
      email: currentUser?.email || 'admin@puertoricowindows.com',
      role: userRole || 'admin',
      name: currentUser?.displayName || null,
    };

    if (editingProposal) {
      try {
        const ref = doc(db, 'proposalTokens', editingProposal.id);
        await updateDoc(ref, {
          clientInfo,
          items,
          total,
          deposit: depositNumber,
          remaining,
          updatedAt: serverTimestamp(),
          updatedBy: createdBy,
        });
        alert('Propuesta actualizada.');
        resetForm();
        await fetchProposals(client.email);
      } catch (err) {
        console.error('Error al actualizar propuesta:', err);
        alert('No se pudo actualizar la propuesta.');
      }
      return;
    }

    const payload = {
      clientInfo,
      items,
      total,
      deposit: depositNumber,
      remaining,
      createdBy,
    };
    
    try {
      const res = await fetch(buildFunctionUrl('/sendProjectProposal'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (res.ok) {
        alert('Propuesta enviada.');
        resetForm();
        await fetchProposals(client.email);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (err) {
      console.error('Error al enviar propuesta:', err);
      alert('Error al enviar.');
    }
  };

  if (!client) return <p className="text-center text-gray-500">Cargando cliente...</p>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto bg-white shadow rounded-lg p-8">
        <div className="flex items-center gap-3 mb-6">
          <InformationCircleIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-semibold text-gray-800">Propuesta para el Cliente</h2>
        </div>

        {/* Información del Cliente */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Información del Cliente</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Nombre</label>
              <input type="text" value={client.name} disabled className="input input-bordered w-full bg-gray-100" />
            </div>
            <div>
              <label className="text-sm text-gray-600">Teléfono</label>
              <input type="text" value={client.phone} disabled className="input input-bordered w-full bg-gray-100" />
            </div>
            <div>
              <label className="text-sm text-gray-600">Correo</label>
              <input type="text" value={client.email} disabled className="input input-bordered w-full bg-gray-100" />
            </div>
          </div>
        </div>

        {editingProposal && (
          <div className="mb-6 p-4 rounded border border-yellow-300 bg-yellow-50 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-sm text-yellow-800 font-semibold">
                Editando propuesta #{editingProposal.id.slice(0, 6)}…
              </p>
              <p className="text-xs text-yellow-700">
                Los cambios se guardarán en la misma propuesta y no se reenviarán automáticamente.
              </p>
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="text-sm text-yellow-900 hover:underline"
            >
              Cancelar edición
            </button>
          </div>
        )}

        <div className="mb-8 rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Solicitudes capturadas</h3>
              <p className="text-sm text-gray-500">
                Estas son las medidas y diseños que el cliente envió desde el landing o campañas.
              </p>
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {loadingIntake ? 'Cargando…' : `${intakeQuotes.length} solicitudes`}
            </span>
          </div>

          {loadingIntake ? (
            <p className="mt-4 text-sm text-gray-500">Sincronizando datos…</p>
          ) : intakeQuotes.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">
              Cuando este lead envíe cotizaciones inteligentes desde el landing aparecerán aquí.
            </p>
          ) : (
            <div className="mt-4 space-y-3">
              {intakeQuotes.map((quote) => {
                const created = quote.capturedAt?.toDate?.() || quote.capturedAt;
                const models = Array.isArray(quote.payload?.models) ? quote.payload.models : [];
                const estimateTotal = models.reduce(
                  (sum, model) => sum + Number(model.estimate || quote.payload?.windowEstimate || 0),
                  0
                );
                return (
                  <div
                    key={quote.id}
                    className="rounded-2xl border border-white bg-white/70 px-4 py-3 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {(quote.cardInformation && quote.cardInformation[0]) || 'Modelo preliminar'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {created?.toLocaleDateString
                            ? created.toLocaleDateString('es-PR', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : 'Fecha pendiente'}{' '}
                          · {quote.source || 'landing'}
                        </p>
                        <p className="text-xs font-semibold text-emerald-600">
                          Estimado preliminar: {formatCurrency(estimateTotal)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleImportIntakeQuote(quote)}
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                      >
                        Usar como propuesta
                      </button>
                    </div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Modelos</p>
                        <ul className="mt-1 text-xs text-gray-700">
                          {models.length ? (
                            models.map((model, index) => (
                              <li key={`${quote.id}-model-${index}`}>
                                {model.name || 'Modelo'} · {model.quantity || 1} uds ·{' '}
                                {model.height && model.width
                                  ? `${model.height}" × ${model.width}"`
                                  : 'Medidas por confirmar'}
                              </li>
                            ))
                          ) : (
                            <li>Sin modelos detallados.</li>
                          )}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notas</p>
                        <p className="text-xs text-gray-700">
                          {quote.notes || quote.payload?.notes || 'Sin notas adicionales'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Servicios */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Servicios Propuestos</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-700 border">
                <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-2 text-left">Servicio</th>
                    <th className="px-4 py-2 text-left">Descripción</th>
                    <th className="px-4 py-2 text-center">Cantidad</th>
                    <th className="px-4 py-2 text-right">Precio</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    <th className="px-4 py-2 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">
                        <input
                          value={item.name}
                          onChange={(e) => handleItemChange(i, 'name', e.target.value)}
                          required
                          className="input input-sm w-full"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          value={item.description}
                          onChange={(e) => handleItemChange(i, 'description', e.target.value)}
                          required
                          className="input input-sm w-full"
                        />
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(i, 'quantity', e.target.value)}
                          className="input input-sm w-20 text-center"
                          min="1"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <input
                          type="number"
                          value={item.price}
                          onChange={(e) => handleItemChange(i, 'price', e.target.value)}
                          className="input input-sm w-24 text-right"
                          step="0.01"
                        />
                      </td>
                      <td className="p-2 text-right font-semibold">
                        ${(item.price && item.quantity) ? (item.price * item.quantity).toFixed(2) : '0.00'}
                      </td>
                      <td className="p-2 text-center">
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(i)}
                            className="text-red-500 hover:text-red-700"
                            title="Eliminar"
                          >
                            <TrashIcon className="w-5 h-5 inline" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addItem}
              className="mt-4 inline-flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              <PlusIcon className="w-5 h-5" />
              Añadir Servicio
            </button>
          </div>

          {/* Resumen Financiero */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Resumen Financiero</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-600">Depósito</label>
                <input
                  type="number"
                  value={deposit}
                  onChange={(e) => setDeposit(e.target.value)}
                  className="input input-bordered w-full"
                  step="0.01"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Total</label>
                <input
                  type="number"
                  value={total.toFixed(2)}
                  readOnly
                  className="input input-bordered w-full bg-gray-100"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Saldo Restante</label>
                <input
                  type="number"
                  value={remaining.toFixed(2)}
                  readOnly
                  className="input input-bordered w-full bg-gray-100"
                />
              </div>
            </div>
          </div>

          {/* Botón de enviar */}
          <div className="pt-6 border-t flex justify-end">
            <button
              type="submit"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-3 rounded transition"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
              {editingProposal ? 'Guardar Cambios' : 'Enviar Propuesta'}
            </button>
          </div>
        </form>

        <section className="mt-10">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Propuestas del Cliente</h3>
          {loadingProposals ? (
            <p className="text-sm text-gray-500">Cargando propuestas…</p>
          ) : proposals.length === 0 ? (
            <p className="text-sm text-gray-500">Este cliente aún no tiene propuestas registradas.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left border">
                <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2">Total</th>
                    <th className="px-4 py-2">Depósito</th>
                    <th className="px-4 py-2">Saldo</th>
                    <th className="px-4 py-2">Estado</th>
                    <th className="px-4 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((proposal) => {
                    const createdAt = proposal.createdAt?.toDate?.() || proposal.createdAt;
                    const totalValue = parseFloat(proposal.total || 0);
                    const depositValue = parseFloat(proposal.deposit || 0);
                    const remainingValue =
                      proposal.remaining !== undefined
                        ? parseFloat(proposal.remaining)
                        : totalValue - depositValue;

                    return (
                      <tr key={proposal.id} className="border-t">
                        <td className="px-4 py-2">
                          {createdAt?.toLocaleDateString
                            ? createdAt.toLocaleDateString('es-PR')
                            : '—'}
                        </td>
                        <td className="px-4 py-2">${totalValue.toFixed(2)}</td>
                        <td className="px-4 py-2">${depositValue.toFixed(2)}</td>
                        <td className="px-4 py-2">${remainingValue.toFixed(2)}</td>
                        <td className="px-4 py-2 capitalize">{proposal.status || 'pendiente'}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleEditProposal(proposal)}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminProposal;
